import { ReactElement, useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Box, Grid, SxProps, Theme, Typography } from "@mui/material";

import NavBar from "../navbar/navbar";
import CouponForm from "./components/couponform/couponform";
import SelectedFixture from "./components/selectedfixture/selectedfixture";
import useDetailRound from "../rounds/useDetailRound";
import useInterval from "react-useinterval";
import { RoundInformation } from "./components/roundinformation";
import Progress from "../common/progress";
import SurvivorRound from "./survivorround";
import { isFixtureAfterGameStart } from "../common/DateTimeHelper";
import { BackIconGreen, GreyedOutFill } from "../reusable-components";
import { useTranslation } from "react-i18next";
// @ts-ignore -- untyped module
import { useMediaQuery } from "react-responsive/src";
import useDetailedFixture from "../fixtures/useDetailedFixture";
import { usePlayer } from "../player/playerContext";
import { ProPickSelector } from "./components/propickselector";
import ReducedSystemSelector from "./components/reducedsystemselector";
import { DetailedRound } from "../models/round";
import { Fixture } from "../models/fixture";
import { GetProPickLabel, themeC } from "../theme/themehelper";
import TheGameBetView from "./components/thegame/thegamebetview";
import TheGameSpectatorView from "./components/thegame/thegamespectatorview";
import { getTheGameManifest } from "./components/thegame/thegamemanifest";
import { parseCouponSigns } from "../common/couponDeeplink";
import { track, trackRoundOpened } from "../analytics/analytics";

/* ------------------------------------------------------------------ */
/*  Sx styles                                                         */
/* ------------------------------------------------------------------ */

const wrapperSx: SxProps<Theme> = {
  marginLeft: "var(--coupon-left-margin)",
};

const wrapperMobileSx: SxProps<Theme> = {
  marginLeft: 0,
};

const flexDivSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "var(--flex-direction)" as any,
};

const flexDivMobileSx: SxProps<Theme> = {
  flexDirection: "column",
  marginTop: "var(--mobile-margin-top)",
};

const leftFlexSx: SxProps<Theme> = {
  minWidth: 402,
};

const rightFlexSx: SxProps<Theme> = {
  width: "100%",
};

const rightFlexMobileSx: SxProps<Theme> = {
  display: "none",
};

const noContentWrapperSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: "120px 0",
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                    */
/* ------------------------------------------------------------------ */

interface ContentAreaProps {
  fixture?: Fixture;
  theme: string;
  round: DetailedRound;
}

function ContentArea({ fixture, theme, round }: ContentAreaProps): ReactElement {
  const fixtureId = fixture ? fixture.id : "";
  const { fixtureDetails } = useDetailedFixture(fixtureId);

  if (fixture) {
    const isAfterGameStart = isFixtureAfterGameStart(fixture);
    return (
      <SelectedFixture
        fixtureDetail={fixtureDetails}
        isAfterGameStart={isAfterGameStart}
        roundFixture={fixture}
        theme={theme}
        round={round}
      />
    );
  }

  return (
    <Box sx={noContentWrapperSx}>
      <Progress />
    </Box>
  );
}

/* ------------------------------------------------------------------ */

interface ProPickSuggestion {
  dif: string | null;
  numFull: number;
  numHalf: number;
}

interface RoundContentProps {
  round: DetailedRound;
  proPickSuggestion: ProPickSuggestion;
  prefillSigns: string | null;
}

function RoundContent({ round, proPickSuggestion, prefillSigns }: RoundContentProps): ReactElement {
  const isSmallScreen = useMediaQuery({ query: "(max-width: 950px)" });
  const initialFixtureIndexSelected = isSmallScreen ? -1 : 0;

  const [fixtureValue, onChangeFixture] = useState<number>(initialFixtureIndexSelected);
  const [showRoundInfo, setShowRoundInfo] = useState(false);
  const [showProPick, setShowProPick] = useState(false);
  const [showRSystem, setShowRSystem] = useState(false);
  const [proPickRows, setProPickRows] = useState<any>(() => parseCouponSigns(prefillSigns, round.fixtures.length));
  const [chosenRSystem, setChosenRSystem] = useState<any>({ type: "MATHEMATICAL" });
  const { t } = useTranslation();

  const fixture = fixtureValue === undefined ? undefined : round.fixtures[fixtureValue];

  useEffect(() => {
    if (showRoundInfo) {
      track("rules_opened", { round_id: round.id });
    }
  }, [showRoundInfo, round.id]);

  const onChangeFixtureAndClose = (params: number): void => {
    onChangeFixture(params);
    setShowRoundInfo(false);
  };

  const leftFlexContent = (): ReactElement => {
    const pausedScreenWidth = isSmallScreen ? "100%" : "402px";

    const pausedScreen = (
      <GreyedOutFill theme={{ width: pausedScreenWidth, height: "100%" }}>
        <Box sx={{ height: "100%", marginTop: "210px" }}>
          <Typography variant="h5" sx={{ color: "white", fontWeight: "400", textAlign: "center" }}>
            {t("round_paused_explanation")}
          </Typography>
        </Box>
      </GreyedOutFill>
    );

    return (
      <>
        {round.status === "PAUSED" ? pausedScreen : <Box />}
        <CouponForm
          onChangeFixture={onChangeFixtureAndClose}
          round={round}
          setShowRoundInfo={setShowRoundInfo}
          showRoundInfo={showRoundInfo}
          proPickSuggestion={proPickSuggestion}
          setShowProPick={setShowProPick}
          showProPick={showProPick}
          setProPickRows={setProPickRows}
          proPickRows={proPickRows}
          setChosenRSystem={setChosenRSystem}
          chosenRSystem={chosenRSystem}
          showRSystem={showRSystem}
          setShowRSystem={setShowRSystem}
        />
      </>
    );
  };

  let rightContent: ReactElement = (
    <ContentArea fixture={fixture} theme={window.theme} round={round} />
  );

  if (!isSmallScreen) {
    if (showRoundInfo) {
      rightContent = <RoundInformation round={round} setShowRoundInfo={setShowRoundInfo} />;
    }
    if (showProPick) {
      rightContent = (
        <ProPickSelector
          setProPickRows={(y: any) => setProPickRows(y)}
          setShowProPick={(x: boolean) => setShowProPick(x)}
          fixtures={round.fixtures}
          operatorStakePerRow={round.operatorStakePerRow ?? { amount: 0, currency: '' }}
          operatorFeePerRow={round.operatorFeePerRow ?? { amount: 0, currency: '' }}
          proPickName={GetProPickLabel(window.theme, t)}
        />
      );
    }
    if (showRSystem) {
      rightContent = (
        <ReducedSystemSelector
          reducedSystems={round.reducedSystems}
          onReset={() => {
            setChosenRSystem({ type: "MATHEMATICAL" });
            setShowRSystem(false);
          }}
          onChange={(id: string) => {
            setChosenRSystem({ type: "REDUCED", reducedSystemId: id });
            setShowRSystem(false);
          }}
          numGames={round.fixtures.length}
          chosenRSystem={chosenRSystem}
        />
      );
    }
  }

  return (
    <Box sx={{ ...flexDivSx, ...(isSmallScreen ? flexDivMobileSx : {}) } as SxProps<Theme>}>
      <Box sx={isSmallScreen ? {} : leftFlexSx}>
        {leftFlexContent()}
      </Box>
      <Box sx={{ ...rightFlexSx, ...(isSmallScreen ? rightFlexMobileSx : {}) } as SxProps<Theme>}>
        {rightContent}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Hook                                                              */
/* ------------------------------------------------------------------ */

function useQueryParams(): URLSearchParams {
  return new URLSearchParams(useLocation().search);
}

/* ------------------------------------------------------------------ */
/*  Main exported component                                           */
/* ------------------------------------------------------------------ */

export default function RoundView(): ReactElement | null {
  const { roundId } = useParams<{ roundId: string }>();
  const { status, error, round, reloadDetailRound } = useDetailRound(roundId);
  const queryParams = useQueryParams();
  const history = useHistory();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_player, config] = usePlayer() as [any, any];
  const [id, setId] = useState(0);
  const isSmallScreen = useMediaQuery({ query: "(max-width: 950px)" });
  const theme = window.theme;
  const isGreenTheme = theme === themeC.Green;

  const hasAppHistory = !!(location as any).key;

  const goBack = () => {
    if (hasAppHistory) {
      history.goBack();
    } else {
      history.push("/clear");
    }
  };

  const dif = queryParams.get("dif");
  const numFull = queryParams.get("numFull");
  const numHalf = queryParams.get("numHalf");
  const prefillSigns = queryParams.get("signs");
  const showBalance = config && config.showPlayerBalance;

  const proPickSuggestion: ProPickSuggestion = {
    dif,
    numFull: parseInt(numFull ?? "0", 10),
    numHalf: parseInt(numHalf ?? "0", 10),
  };

  useInterval(() => {
    reloadDetailRound();
    if (showBalance) {
      setId((prev: number) => prev + 1);
    }
  }, 20000);

  useEffect(() => {
    if (error) {
      history.push("/clear");
    }
  }, [error, history]);

  const openedRoundId = round?.id;
  const openedRoundName = round?.name;
  useEffect(() => {
    if (openedRoundId !== undefined && openedRoundName !== undefined) {
      trackRoundOpened(openedRoundId, openedRoundName);
    }
  }, [openedRoundId, openedRoundName]);

  if (status === "loading") {
    return (
      <>
        {/* @ts-ignore -- NavBar is untyped JS, props are optional */}
        <NavBar />
        <Box sx={noContentWrapperSx}>
          <Progress />
        </Box>
      </>
    );
  }

  if (!round) {
    return null;
  }

  const isSurvivor = round.name.toLowerCase().indexOf("survivor") !== -1;
  const isAfterDeadline = new Date(round.openTo) < new Date();
  const showSurvivorView = isSurvivor && isAfterDeadline;
  const theGameManifest = getTheGameManifest(round);

  return (
    <>
      {/* @ts-ignore -- NavBar is untyped JS, props are optional */}
      <NavBar balanceUpdateId={id} />
      {isGreenTheme && (
        <Grid sx={{ margin: '4px 0px 12px 16px', cursor: 'pointer' }} onClick={goBack}>
          {BackIconGreen()}
        </Grid>
      )}
      <Box component="main" sx={{ ...wrapperSx, ...(isSmallScreen ? wrapperMobileSx : {}) } as SxProps<Theme>}>
        {theGameManifest ? (
          isAfterDeadline || round.status === "FINISHED" ? (
            <TheGameSpectatorView round={round} manifest={theGameManifest} />
          ) : (
            <TheGameBetView round={round} manifest={theGameManifest} />
          )
        ) : showSurvivorView ? (
          <SurvivorRound round={round as DetailedRound} />
        ) : (
          <RoundContent round={round} proPickSuggestion={proPickSuggestion} prefillSigns={prefillSigns} />
        )}
      </Box>
    </>
  );
}
